// <AnniversariesCard> — Home View Step 4 (T-043, plan §3 / spec §7).
//
// Horizontal-scroll carousel of upcoming anniversaries (next 30 days,
// year-1+, server-filtered + sorted). Each entry: avatar, name,
// month-day, "N years" badge.

import { Link } from 'react-router-dom';
import type { HomeAnniversary } from '@pa/shared';
import { Avatar } from './Avatar';

export interface AnniversariesCardProps {
  anniversaries: HomeAnniversary[];
}

export function AnniversariesCard({ anniversaries }: AnniversariesCardProps) {
  return (
    <section
      data-testid="home-anniversaries"
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
    >
      <header className="p-4 border-b border-slate-100 flex justify-between items-center">
        <h3 className="text-sm font-bold text-slate-800">Anniversaries</h3>
      </header>
      {anniversaries.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-400 italic">
          No upcoming anniversaries
        </div>
      ) : (
        <ul className="p-6 flex gap-6 overflow-x-auto no-scrollbar">
          {anniversaries.map((a) => (
            <li
              key={a.id}
              className="flex flex-col items-center text-center min-w-[90px] flex-shrink-0"
            >
              <Link
                to={`/employees/${a.id}`}
                className="flex flex-col items-center text-center group"
              >
                <Avatar
                  name={a.name}
                  avatarUrl={a.avatarUrl}
                  sizeClass="w-14 h-14"
                  className="border-2 border-slate-100 mb-2"
                />
                <p className="text-xs font-bold text-slate-800 leading-tight group-hover:text-[#6344C6]">
                  {a.name}
                </p>
                <p className="text-[10px] text-slate-400 font-bold mt-0.5">
                  {formatMonthDay(a.startDate)}
                </p>
                <p className="text-[10px] text-[#6344C6] font-semibold">
                  {a.yearsAtOrg} {a.yearsAtOrg === 1 ? 'year' : 'years'}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// `YYYY-MM-DD` → `<Month> <Day>` (e.g. "March 1") per spec §7.2.
function formatMonthDay(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const monthIdx = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (monthIdx < 0 || monthIdx > 11) return iso;
  return `${MONTHS[monthIdx]} ${day}`;
}
